.split {
  flex: 1;
  min-height: 0;
  display: flex;
  border-top: 1px solid var(--c-border);
}

.list {
  flex: none;
  display: flex;
  flex-direction: column;
  min-height: 0;
  border-right: 1px solid var(--c-border);
  overflow: hidden;
}

/* Collapsed by the handle: nothing of it shows, and its border goes with it so the handle's line
   is the only one left. */
.collapsed {
  border-right: 0;
}

/* The divider as a handle: a hit area wide enough to grab, drawn as the one-pixel line the layout
   always had, with the grip appearing on hover and focus. It sits over the list's border so the
   line does not double. */
.handle {
  flex: none;
  position: relative;
  width: 9px;
  margin-left: -5px;
  margin-right: -4px;
  z-index: 2;
  cursor: col-resize;
  display: flex;
  align-items: center;
  justify-content: center;
  touch-action: none;
  user-select: none;
  outline: none;
}

.handle::before {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  left: 4px;
  width: 1px;
  background: var(--c-border);
  transition:
    background 0.12s,
    width 0.12s,
    left 0.12s;
}

.handle:hover::before,
.handle:focus-visible::before,
.dragging .handle::before {
  left: 3px;
  width: 3px;
  background: var(--c-brand);
}

.handleCollapsed {
  width: 18px;
  margin-left: 0;
  cursor: pointer;
}

.handleCollapsed::before {
  left: 0;
}

.grip {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 36px;
  border-radius: 8px;
  color: var(--c-text-secondary);
  background: var(--c-surface);
  border: 1px solid var(--c-border);
  opacity: 0;
  transition: opacity 0.12s;
}

.handle:hover .grip,
.handle:focus-visible .grip,
.dragging .grip,
.handleCollapsed .grip {
  opacity: 1;
}

.dragging {
  cursor: col-resize;
}

/* While a drag is under way the panes must not swallow the pointer or select their text. */
.dragging .list,
.dragging .detail {
  pointer-events: none;
  user-select: none;
}

.listHeader {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 20px;
  border-bottom: 1px solid var(--c-border);
  flex: none;
}

.listBody {
  flex: 1;
  min-height: 0;
  overflow: auto;
}

.detail {
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: auto;
}

.pane {
  padding: 18px 24px 32px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
}

.item {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-height: 56px;
  padding: 8px 20px;
  border: 0;
  border-bottom: 1px solid var(--c-border);
  background: transparent;
  text-align: left;
  color: var(--c-text-primary);
}

.item:hover {
  background: var(--c-overlay);
}

.selected,
.selected:hover {
  background: var(--c-brand-subtle);
  box-shadow: inset 3px 0 0 var(--c-brand);
}

.glow {
  box-shadow: inset 3px 0 0 var(--c-brand-vivid);
}

.selected.glow {
  box-shadow:
    inset 3px 0 0 var(--c-brand),
    inset 0 0 18px -8px var(--glow);
}

/* A list whose rows can be picked: room for the box in the row's left padding. */
.checkable {
  position: relative;
}

.checkable .item {
  padding-left: 36px;
}

.itemCheck {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;
  opacity: 0;
  transition: opacity 0.12s;
}

.checkable:hover .itemCheck,
.itemCheck:focus-visible,
.picking .itemCheck {
  opacity: 1;
}

/* No hover on a touch screen, so the boxes would never appear: there they are always shown. */
@media (hover: none) {
  .itemCheck {
    opacity: 1;
  }
}

.picked .item {
  background: color-mix(in srgb, var(--c-brand) 10%, transparent);
}

/* --- SidePanelLayout: main content with a collapsible panel on the right, inside a page. --- */
.sideSplit {
  display: flex;
  align-items: stretch;
  min-width: 0;
}

.sideMain {
  flex: 1;
  min-width: 0;
}

/* The handle's own margins centre its hairline in the gap between the two panels. */
.sideHandle {
  width: 16px;
  margin: 0;
}

.sideHandle::before {
  left: 7px;
}

.sideHandle:hover::before,
.sideHandle:focus-visible::before,
.dragging .sideHandle::before {
  left: 6px;
}

/* Collapsed, the handle is the way back: a full-height tab wide enough to show the open chevron,
   tinted as a whole on hover and focus, and reopening from a click anywhere on it (the whole tab is
   the handle). The hairline and the grip's own box give way to the tab's border and fill. */
.sideHandle.handleCollapsed {
  width: 32px;
  margin-left: 8px;
  align-self: stretch;
  border: 1px solid var(--c-border);
  border-radius: var(--radius-md);
  background: var(--c-surface);
  color: var(--c-text-secondary);
  transition:
    background 0.12s,
    border-color 0.12s,
    color 0.12s;
}

.sideHandle.handleCollapsed::before {
  display: none;
}

/* A panel on the left collapses to a tab on the left edge, the gap on its other side. */
.sideLeft .sideHandle.handleCollapsed {
  margin-left: 0;
  margin-right: 8px;
}

.sideHandle.handleCollapsed:hover,
.sideHandle.handleCollapsed:focus-visible {
  background: var(--c-brand-subtle);
  border-color: var(--c-brand);
  color: var(--c-brand);
}

.sideHandle.handleCollapsed .grip {
  width: auto;
  height: auto;
  border: 0;
  background: transparent;
  color: inherit;
}

/* Slides shut to the right and back: the width animates, except under a drag, which follows the
   pointer. */
.sidePane {
  flex: none;
  min-width: 0;
  overflow: hidden;
  transition: width 0.22s ease, opacity 0.22s ease;
}

.sideHalf {
  width: 50%;
}

/* Its height goes too: a zero-width pane still holding the chart's height kept the row — and the
   collapsed tab stretched across it — far taller than the table beside it. */
.sideCollapsed {
  width: 0;
  max-height: 0;
  opacity: 0;
}

.dragging .sidePane {
  transition: none;
}

.dragging .sideMain,
.dragging .sidePane {
  pointer-events: none;
  user-select: none;
}

/* Stacked under the main content on narrow screens; the open handle has nothing to resize there,
   and the collapsed one becomes a full-width bar that brings the panel back. */
@media (max-width: 1100px) {
  .sideSplit {
    flex-direction: column;
    gap: 16px;
  }

  .sidePane,
  .sideHalf {
    width: 100% !important;
    transition: none;
  }

  .sideCollapsed {
    display: none;
  }

  .sideHandle {
    display: none;
  }

  .sideHandle.handleCollapsed {
    display: flex;
    width: 100%;
    height: 22px;
    margin: 0;
  }

  .sideHandle.handleCollapsed::before {
    top: auto;
    bottom: 0;
    left: 0;
    right: 0;
    width: auto;
    height: 1px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .sidePane {
    transition: none;
  }
}
